<template>
  <validation-observer
    ref="form"
    v-slot="{ invalid }"
  >
    <am-modal
      :show="modalOpened"
      :title="employee && employee.id ? 'Edit staff / executive' : 'Register staff or union executive'"
      size="lg"
      :loading="saving"
      :ok-disabled="invalid || saving"
      ok-text="Save"
      @onClose="$emit('modalClosed')"
      @onOk="save"
    >
      <div class="row">
        <div class="col-md-4">
          <am-select
            v-model="form.employee_type"
            name="employee_type"
            label="Employee type"
            rules="required"
            label-name="text"
            value-name="value"
            :options="typeOptions"
          />
        </div>
        <div class="col-md-4">
          <am-input
            v-model="form.first_name"
            name="first_name"
            label="First name"
            rules="required"
          />
        </div>
        <div class="col-md-4">
          <am-input
            v-model="form.last_name"
            name="last_name"
            label="Last name"
            rules="required"
          />
        </div>
        <div class="col-md-4">
          <am-select
            v-model="form.gender"
            name="gender"
            label="Gender"
            :options="genderOptions"
          />
        </div>

        <template v-if="isStaff">
          <div class="col-12 mt-1"><h5>Employment information</h5></div>
          <div class="col-md-4">
            <am-input
              v-model="form.position"
              name="position"
              label="Role / position"
              rules="required"
            />
          </div>
          <div class="col-md-4">
            <am-select
              v-model="form.department_id"
              name="department_id"
              label="Department"
              rules="required"
              label-name="name"
              value-name="id"
              :options="departments"
            />
          </div>
          <div class="col-md-4">
            <am-date-picker
              v-model="form.employment_date"
              name="employment_date"
              label="Date employed"
              rules="required"
            />
          </div>
          <div class="col-md-4">
            <am-select
              v-model="form.status"
              name="status"
              label="Employment status"
              :options="staffStatusOptions"
            />
          </div>
          <div
            v-if="form.status === 'Inactive'"
            class="col-md-4"
          >
            <am-select
              v-model="form.inactive_reason"
              name="inactive_reason"
              label="Inactive Reason"
              rules="required"
              :options="inactiveReasonOptions"
            />
          </div>
        </template>

        <template v-else>
          <div class="col-12 mt-1"><h5>Leadership term</h5></div>
          <div class="col-md-4">
            <am-input
              v-model="form.position"
              name="position"
              label="Position"
              rules="required"
            />
          </div>
          <div class="col-md-4">
            <am-input
              v-model="form.committee"
              name="committee"
              label="Committee"
            />
          </div>
          <div class="col-md-4">
            <am-date-picker
              v-model="form.date_elected_appointed"
              name="date_elected_appointed"
              label="Date elected / appointed"
              rules="required"
            />
          </div>
          <div class="col-md-4">
            <am-input
              v-model="form.appointment_method"
              name="appointment_method"
              label="Election or appointment method"
            />
          </div>
          <div class="col-md-4">
            <am-date-picker
              v-model="form.term_start"
              name="term_start"
              label="Term start"
            />
          </div>
          <div class="col-md-4">
            <am-date-picker
              v-model="form.term_end"
              name="term_end"
              label="Term end"
            />
          </div>
          <div class="col-md-4">
            <am-select
              v-model="form.term_status"
              name="term_status"
              label="Term status"
              :options="termStatusOptions"
            />
          </div>
          <div class="col-md-4">
            <am-select
              v-model="form.eligible_reelection"
              name="eligible_reelection"
              label="Eligible for re-election"
              :options="['Yes', 'No', 'Not yet determined']"
            />
          </div>
          <div class="col-md-4">
            <am-input
              v-model="form.previous_position"
              name="previous_position"
              label="Previous position"
            />
          </div>
        </template>

        <div class="col-md-4">
          <am-input
            v-model="form.mobile"
            name="mobile"
            label="Mobile contact"
            rules="required"
          />
        </div>
        <div class="col-md-4">
          <am-input
            v-model="form.email"
            name="email"
            label="Work email"
          />
        </div>
        <div class="col-md-12">
          <am-text-area
            v-model="form.address"
            name="address"
            label="Residential / contact address"
            rules="required"
            rows="2"
          />
        </div>

        <div class="col-12 mt-1"><h5>Emergency contact</h5></div>
        <div class="col-md-4">
          <am-input
            v-model="form.emergency_name"
            name="emergency_name"
            label="Contact name"
            rules="required"
          />
        </div>
        <div class="col-md-4">
          <am-input
            v-model="form.emergency_relation"
            name="emergency_relation"
            label="Relationship"
            rules="required"
          />
        </div>
        <div class="col-md-4">
          <am-input
            v-model="form.emergency_phone"
            name="emergency_phone"
            label="Emergency mobile"
            rules="required"
          />
        </div>
        <div class="col-md-12">
          <am-text-area
            v-model="form.notes"
            name="notes"
            label="Public role notes"
            rows="2"
          />
        </div>

        <div class="col-12">
          <error-display :error="error" />
        </div>
      </div>
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="$emit('modalClosed')"
          />
          <am-button
            name="save_employee"
            label="Save"
            :disabled="invalid || saving"
            :loading="saving"
            loading-text="Loading..."
            @click="save"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { BRow } from 'bootstrap-vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import AmTextArea from '@/farmbusiness/components/AmTextArea.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import showToast from '@/farmbusiness/mixins/showToast'

const emptyForm = () => ({
  employee_type: 'staff',
  first_name: null,
  last_name: null,
  gender: null,
  mobile: null,
  email: null,
  address: null,
  emergency_name: null,
  emergency_relation: null,
  emergency_phone: null,
  notes: null,
  status: 'Active',
  inactive_reason: null,
  position: null,
  department_id: null,
  employment_date: null,
  committee: null,
  date_elected_appointed: null,
  appointment_method: null,
  term_start: null,
  term_end: null,
  term_status: 'Serving',
  eligible_reelection: null,
  previous_position: null,
})

export default {
  name: 'EmployeeSetup',
  components: {
    ValidationObserver,
    AmModal,
    AmInput,
    AmSelect,
    AmDatePicker,
    AmTextArea,
    AmButton,
    ErrorDisplay,
    BRow,
  },
  mixins: [showToast],
  props: {
    modalOpened: { type: Boolean, default: false },
    employee: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      saving: false,
      error: {},
      typeOptions: [
        { text: 'Staff', value: 'staff' },
        { text: 'Union Executive', value: 'union_executive' },
      ],
      genderOptions: ['Male', 'Female', 'Other', 'Prefer not to say'],
      staffStatusOptions: ['Active', 'On leave', 'Inactive'],
      inactiveReasonOptions: ['Resigned', 'Contract Terminated', 'Retired', 'Deceased'],
      termStatusOptions: ['Serving', 'Completed', 'Stepped down'],
    }
  },
  computed: {
    isStaff() {
      return this.form.employee_type === 'staff'
    },
    departments() {
      return this.$store.getters['employeeCentre/departments'] || []
    },
  },
  watch: {
    modalOpened(opened) {
      if (opened) this.bootstrap()
    },
  },
  methods: {
    bootstrap() {
      this.error = {}
      if (this.employee && this.employee.id) {
        this.form = {
          ...emptyForm(),
          ...this.employee,
          department_id: this.employee.department_id || (this.employee.department && this.employee.department.id) || null,
        }
      } else {
        this.form = emptyForm()
      }
      if (!this.departments.length) {
        this.$store.dispatch('employeeCentre/fetchDepartments')
      }
    },
    save() {
      this.$refs.form.validate().then(ok => {
        if (!ok) return
        this.saving = true
        this.error = {}
        const payload = { ...this.form }
        if (payload.employee_type === 'staff') {
          payload.term_status = null
          payload.committee = null
          payload.date_elected_appointed = null
          payload.appointment_method = null
          payload.term_start = null
          payload.term_end = null
          payload.eligible_reelection = null
          payload.previous_position = null
          if (payload.status !== 'Inactive') {
            payload.inactive_reason = null
          }
        } else {
          payload.department_id = null
          payload.employment_date = null
          payload.inactive_reason = null
          payload.status = payload.term_status
        }
        const action = this.employee && this.employee.id
          ? this.$store.dispatch('employeeCentre/updateEmployee', { id: this.employee.id, data: payload })
          : this.$store.dispatch('employeeCentre/createEmployee', payload)
        action.then(() => {
          this.showSuccess('Saved successfully')
          this.$emit('saved')
        }).catch(err => {
          this.error = err.errors || { detail: err.message || 'Failed to save' }
          this.showError('Failed to save')
        }).finally(() => { this.saving = false })
      })
    },
  },
}
</script>
